// Timezone options with UTC offsets
export const timezoneOptions = [
  { value: 'EST', label: 'EST (Eastern)', offset: '-05:00' },
  { value: 'CST', label: 'CST (Central)', offset: '-06:00' },
  { value: 'MST', label: 'MST (Mountain)', offset: '-07:00' },
  { value: 'PST', label: 'PST (Pacific)', offset: '-08:00' },
  { value: 'AST', label: 'AST (Alaska)', offset: '-09:00' },
  { value: 'HST', label: 'HST (Hawaii)', offset: '-10:00' },
];

// Initial sales interview form state
export const initialSalesInterview = {
  candidateName: '',
  job: '',
  jobTitle: '',
  technology: '',
  vendor: '',
  clientCompany: '',
  clientName: '',
  interviewDate: '',
  interviewTime: '',
  interviewTimeAmPm: 'AM',
  interviewTimezone: 'CST',
  interviewer1: '',
  interviewer2: '',
  link: '',
  appointmentDetails: '',
  interviewStatus: 'In Progress',
  rating: 0,
  feedback: '',
  masterJobId: '',
  marketingId: '',
};

// Filter categories for the filter popup
export const filterCategories = [
  'Job',
  'Candidate',
  'Client',
  'Vendor',
  'Bill Rate',
  'Interviewer',
  'Rating',
  'Feedback',
];

// Auto-format time input as HH:MM
export const formatTimeInput = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 4);
  if (limited.length <= 2) {
    return limited;
  }
  return `${limited.slice(0, 2)}:${limited.slice(2)}`;
};

// Helper to convert 12-hour time to 24-hour format
export const convertTo24Hour = (time, amPm) => {
  if (!time) return { hours: '00', minutes: '00' };
  const timeParts = time.split(':');
  let hours = parseInt(timeParts[0]) || 0;
  const minutes = timeParts[1] || '00';

  if (amPm === 'PM' && hours !== 12) {
    hours = hours + 12;
  } else if (amPm === 'AM' && hours === 12) {
    hours = 0;
  }

  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
  };
};

// Helper to format time to HH:MM:SS+ZZ:ZZ format with timezone
export const formatInterviewTime = (time, amPm, timezone) => {
  if (!time) return null;
  const { hours, minutes } = convertTo24Hour(time, amPm);
  const seconds = '00';
  const selectedTz = timezoneOptions.find((tz) => tz.value === timezone);
  const tzOffset = selectedTz ? selectedTz.offset : '-06:00';
  return `${hours}:${minutes}:${seconds}${tzOffset}`;
};

// Format date from YYYY-MM-DD to MM/DD/YYYY for API
export const formatDateForAPI = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Helper to parse date from API format (MM/DD/YYYY) to input format (YYYY-MM-DD)
export const parseApiDate = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  return dateString;
};

// Helper to parse rating from API format (e.g., "4/5" or "4")
export const parseApiRating = (rating) => {
  if (!rating) return 0;
  if (typeof rating === 'number') return rating;
  if (typeof rating === 'string') {
    if (rating.includes('/')) {
      return parseInt(rating.split('/')[0]) || 0;
    }
    return parseInt(rating) || 0;
  }
  return 0;
};

// Helper to parse 24-hour time to 12-hour format with timezone
export const parse24HourTime = (timeString) => {
  if (!timeString) return { time: '', amPm: 'AM', timezone: 'CST' };

  const timeParts = timeString.split(':');
  let hours24 = parseInt(timeParts[0]) || 0;
  const minutes = timeParts[1] || '00';

  let time = '';
  let amPm = 'AM';

  if (hours24 === 0) {
    time = `12:${minutes}`;
    amPm = 'AM';
  } else if (hours24 === 12) {
    time = `12:${minutes}`;
    amPm = 'PM';
  } else if (hours24 > 12) {
    time = `${String(hours24 - 12).padStart(2, '0')}:${minutes}`;
    amPm = 'PM';
  } else {
    time = `${String(hours24).padStart(2, '0')}:${minutes}`;
    amPm = 'AM';
  }

  // Extract timezone offset
  let timezone = 'CST';
  const offsetMatch = timeString.match(/([+-]\d{2}:\d{2})$/);
  if (offsetMatch) {
    const offset = offsetMatch[1];
    const matchedTz = timezoneOptions.find((tz) => tz.offset === offset);
    if (matchedTz) {
      timezone = matchedTz.value;
    }
  }

  return { time, amPm, timezone };
};

// Prepare interview data for editing from API response
export const prepareInterviewForEdit = (interview) => {
  if (!interview) return null;

  // Parse interview panel details (comma separated names)
  const [interviewer1 = '', interviewer2 = ''] = (interview.interview_panel_details || '')
    .split(',')
    .map((name) => name.trim());

  // Parse date from API format
  const interviewDate = parseApiDate(interview.interview_date);

  // Parse time from API format
  const { time, amPm, timezone } = parse24HourTime(interview.interview_time);

  // Parse rating from API format
  const ratingValue = parseApiRating(interview.rating);

  return {
    interviewId: interview.record_id,
    candidateName: interview.candidate_id,
    job: interview.job_id,
    jobTitle: interview.job_title || '',
    technology: interview.technology_id || '',
    vendor: interview.vendor_id || '',
    clientCompany: interview.company || interview.end_client_name || '',
    clientName: interview.sales_person_name || interview.client || '',
    interviewer1: interviewer1,
    interviewer2: interviewer2,
    link: interview.appointment_link || '',
    appointmentDetails: interview.appointment_details || '',
    interviewStatus: interview.scheduling_status || interview.status || 'In Progress',
    rating: ratingValue,
    feedback: interview.feedback || '',
    interviewDate: interviewDate,
    interviewTime: time,
    interviewTimeAmPm: amPm,
    interviewTimezone: timezone,
    masterJobId: interview.master_job_id || '',
    marketingId: interview.marketing_id || '',
    // Audit fields
    created: interview.created || '',
    created_by: interview.created_by || '',
    updated: interview.updated || '',
    updated_by: interview.updated_by || '',
  };
};

// Prepare form data for API submission
export const prepareInterviewPayload = (formData) => {
  const formattedDate = formData.interviewDate ? formatDateForAPI(formData.interviewDate) : null;
  const formattedTime = formData.interviewTime
    ? formatInterviewTime(formData.interviewTime, formData.interviewTimeAmPm, formData.interviewTimezone)
    : null;

  return {
    ...formData,
    interviewDate: formattedDate,
    interviewTime: formattedTime,
    status: formData.interviewStatus,
  };
};
